import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, CircularProgress, Switch, FormControlLabel, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';

const initialState = {
    Code: '', Name: '', AddressLine1: '', AddressLine2: '', Location: '', PinCode: '',
    StateCodeId: '', ContactNumber1: '', ContactNumber2: '', ContactNumber3: '', Email: '',
    GSTNumber: '', PanNumber: '', PaymentBankId: '', BToBPrefix: '', BToCPrefix: '',
    IGSTPrefix: '', IGSTCustomerPrefix: '',
    QuotationPrefix: '', SaleOrderPrefix: '', PurchaseOrderPrefix: '',
    PurchaseReturnPrefix: '', SaleReturnPrefix: '',
    CashOpeningBalance: '', CashOpeningDate: '',
    StockOpeningBalance: '', StockOpeningDate: '', IsEInvoicingEnabled: false,
    IsEWaybillEnabled: false, EinvoicePortalUserName: '', EinvoicePortalPassword: '',
    myGSTcafeCustomerID: '', myGSTcafeApiID: '', myGSTcafeApiSecret: ''
};

const inputStyle = {
    width: '100%', padding: '6px 8px', fontSize: '13px', borderRadius: '6px',
    border: '1px solid #d1d5db', outline: 'none', color: '#1e293b',
    background: 'white', boxSizing: 'border-box', height: '32px'
};

const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '600',
    color: '#374151', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.3px'
};

const sectionHeader = (title) => (
    <div style={{
        background: '#2C3E6E', color: 'white', padding: '5px 10px',
        fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
        letterSpacing: '0.5px', borderRadius: '4px', marginBottom: '10px', marginTop: '14px'
    }}>{title}</div>
);

const BranchCreateModal = ({ open, onClose, onSave, isSaving = false }) => {
    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    };

    // Only allow digits (0-9) for contact number fields
    const handleContactChange = (e) => {
        const { name, value } = e.target;
        const digitsOnly = value.replace(/\D/g, '').slice(0, 15);
        setFormData(prev => ({ ...prev, [name]: digitsOnly }));
        // Show error if user tried to type non-digits
        if (value !== digitsOnly && value.replace(/\d/g, '').length > 0) {
            setErrors(prev => ({ ...prev, [name]: 'Only numbers are allowed (max 15 digits)' }));
        } else {
            setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
        }
    };

    const handleSubmit = () => {
        const errs = {};
        if (!formData.Code?.trim()) errs.Code = true;
        if (!formData.Name?.trim()) errs.Name = true;
        if (formData.Email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) errs.Email = 'Please enter a valid email address';
        if (formData.ContactNumber1?.trim() && !/^\d{10,15}$/.test(formData.ContactNumber1)) errs.ContactNumber1 = 'Enter a valid number (10-15 digits)';
        if (formData.ContactNumber2?.trim() && !/^\d{10,15}$/.test(formData.ContactNumber2)) errs.ContactNumber2 = 'Enter a valid number (10-15 digits)';
        if (formData.ContactNumber3?.trim() && !/^\d{10,15}$/.test(formData.ContactNumber3)) errs.ContactNumber3 = 'Enter a valid number (10-15 digits)';
        if (formData.GSTNumber?.trim() && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(formData.GSTNumber.trim().toUpperCase())) errs.GSTNumber = 'Enter a valid GST number (e.g. 22AAAAA0000A1Z5)';
        if (formData.PanNumber?.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.PanNumber.trim().toUpperCase())) errs.PanNumber = 'Enter a valid PAN number (e.g. AAAAA0000A)';
        if (Object.keys(errs).length) { setErrors(errs); return; }
        onSave(formData);
    };

    const handleClose = () => {
        setFormData(initialState);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{ style: { borderRadius: '10px', overflow: 'hidden' } }}>
            <DialogTitle style={{ padding: 0 }}>
                <div style={{
                    background: '#2C3E6E', padding: '10px 16px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BusinessIcon style={{ fontSize: '18px', color: 'white' }} />
                        <span style={{ fontSize: '14px', fontWeight: '700', color: 'white', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Create New Branch
                        </span>
                    </div>
                    <IconButton onClick={handleClose} size="small" style={{ color: 'white', padding: '4px' }}>
                        <CloseIcon style={{ fontSize: '18px' }} />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent style={{ padding: '12px 16px', background: 'white' }}>

                {/* Basic Information */}
                {sectionHeader('Basic Information')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
                    <div>
                        <label style={labelStyle}>Branch Code <span style={{ color: 'red' }}>*</span></label>
                        <input name="Code" value={formData.Code} onChange={handleChange} maxLength={5}
                            placeholder="e.g. BR001"
                            style={{ ...inputStyle, borderColor: errors.Code ? '#ef4444' : '#d1d5db' }} />
                        {errors.Code && <span style={{ fontSize: '11px', color: '#ef4444' }}>Required</span>}
                    </div>
                    <div>
                        <label style={labelStyle}>Branch Name <span style={{ color: 'red' }}>*</span></label>
                        <input name="Name" value={formData.Name} onChange={handleChange} maxLength={150}
                            placeholder="Enter branch name"
                            style={{ ...inputStyle, borderColor: errors.Name ? '#ef4444' : '#d1d5db' }} />
                        {errors.Name && <span style={{ fontSize: '11px', color: '#ef4444' }}>Required</span>}
                    </div>
                </div>

                {/* Address Details */}
                {sectionHeader('Address Details')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                        <label style={labelStyle}>Address Line 1</label>
                        <input name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={200}
                            placeholder="Street address" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Address Line 2</label>
                        <input name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={200}
                            placeholder="Apartment, suite" style={inputStyle} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                        <label style={labelStyle}>City</label>
                        <input name="Location" value={formData.Location} onChange={handleChange} maxLength={50}
                            placeholder="City name" style={inputStyle} />
                    </div>
                    {/* <div>
                        <label style={labelStyle}>Pin Code</label>
                        <input name="PinCode" value={formData.PinCode} onChange={handleChange} maxLength={6}
                            placeholder="123456" style={inputStyle} />
                    </div> */}

                    <div>
                        <label style={labelStyle}>Pin Code</label>
                        <input
                            name="PinCode"
                            value={formData.PinCode}
                            onChange={(e) => {
                                const value = e.target.value;

                                if (/^\d*$/.test(value) && value.length <= 6) {
                                    handleChange(e);
                                }
                            }}
                            maxLength={6}
                            inputMode="numeric"
                            placeholder="123456"
                            style={inputStyle}
                        />

                        {formData.PinCode && !/^\d{6}$/.test(formData.PinCode) && (
                            <small style={{ color: "red" }}>
                                Please enter a valid 6-digit PIN code.
                            </small>
                        )}
                    </div>
                    <div>
                        <label style={labelStyle}>State Code ID</label>
                        <input name="StateCodeId" type="number" value={formData.StateCodeId} onChange={handleChange}
                            placeholder="State ID" style={inputStyle} />
                    </div>
                </div>

                {/* Contact Details */}
                {sectionHeader('Contact Details')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                        <label style={labelStyle}>Primary Contact</label>
                        <input name="ContactNumber1" value={formData.ContactNumber1} onChange={handleContactChange} maxLength={15}
                            placeholder="XXXXX XXXXX" style={{ ...inputStyle, borderColor: errors.ContactNumber1 ? '#ef4444' : '#d1d5db' }} inputMode="numeric" />
                        {errors.ContactNumber1 && <span style={{ fontSize: '11px', color: '#ef4444' }}>{errors.ContactNumber1}</span>}
                    </div>
                    <div>
                        <label style={labelStyle}>Secondary Contact</label>
                        <input name="ContactNumber2" value={formData.ContactNumber2} onChange={handleContactChange} maxLength={15}
                            placeholder="XXXXX XXXXX" style={{ ...inputStyle, borderColor: errors.ContactNumber2 ? '#ef4444' : '#d1d5db' }} inputMode="numeric" />
                        {errors.ContactNumber2 && <span style={{ fontSize: '11px', color: '#ef4444' }}>{errors.ContactNumber2}</span>}
                    </div>
                    <div>
                        <label style={labelStyle}>Alternate Contact</label>
                        <input name="ContactNumber3" value={formData.ContactNumber3} onChange={handleContactChange} maxLength={15}
                            placeholder="XXXXX XXXXX" style={{ ...inputStyle, borderColor: errors.ContactNumber3 ? '#ef4444' : '#d1d5db' }} inputMode="numeric" />
                        {errors.ContactNumber3 && <span style={{ fontSize: '11px', color: '#ef4444' }}>{errors.ContactNumber3}</span>}
                    </div>
                </div>
                {/* <div>
                    <label style={labelStyle}>Email</label>
                    <input name="Email" type="email" value={formData.Email} onChange={handleChange} maxLength={100}
                        placeholder="branch@company.com" style={inputStyle} />
                </div> */}
       
                <div>
                    <label style={labelStyle}>Email</label>
                    <input
                        name="Email"
                        type="email"
                        value={formData.Email}
                        onChange={(e) => {
                            // Strip spaces — never valid in an email address
                            const value = e.target.value.replace(/\s/g, '');
                            setFormData(prev => ({ ...prev, Email: value }));
                            // Clear error as soon as value becomes valid while typing
                            if (errors.Email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                setErrors(prev => ({ ...prev, Email: '' }));
                            }
                        }}
                        onBlur={() => {
                            const email = formData.Email?.trim() || '';
                            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                                setErrors(prev => ({ ...prev, Email: 'Please enter a valid email address' }));
                            } else {
                                setErrors(prev => ({ ...prev, Email: '' }));
                            }
                        }}
                        maxLength={100}
                        placeholder="branch@company.com"
                        style={{ ...inputStyle, borderColor: errors.Email ? '#ef4444' : '#d1d5db' }}
                    />
                    {errors.Email && (
                        <small style={{ color: '#ef4444' }}>{errors.Email}</small>
                    )}
                </div>
                
                {/* Tax Details */}
                {sectionHeader('Tax Details')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                        <label style={labelStyle}>GST Number</label>
                        <input name="GSTNumber" value={formData.GSTNumber} onChange={(e) => {
                            const val = e.target.value.toUpperCase();
                            setFormData(prev => ({ ...prev, GSTNumber: val }));
                            if (errors.GSTNumber) setErrors(prev => { const n = { ...prev }; delete n.GSTNumber; return n; });
                        }} maxLength={15}
                            placeholder="22AAAAA0000A1Z5" style={{ ...inputStyle, borderColor: errors.GSTNumber || (formData.GSTNumber && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(formData.GSTNumber.toUpperCase())) ? '#ef4444' : '#d1d5db' }} />
                        {(errors.GSTNumber || (formData.GSTNumber && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(formData.GSTNumber.toUpperCase()))) && (
                            <small style={{ color: "red" }}>Enter a valid GST number (e.g. 22AAAAA0000A1Z5)</small>
                        )}
                    </div>
                    <div>
                        <label style={labelStyle}>PAN Number</label>
                        <input
                            name="PanNumber"
                            value={formData.PanNumber}
                            onChange={(e) => {
                                // Force uppercase, strip non-alphanumeric
                                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                setFormData(prev => ({ ...prev, PanNumber: value }));
                                // Clear error once valid while typing
                                if (errors.PanNumber && /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
                                    setErrors(prev => ({ ...prev, PanNumber: '' }));
                                }
                            }}
                            onBlur={() => {
                                const pan = formData.PanNumber?.trim() || '';
                                if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) {
                                    setErrors(prev => ({ ...prev, PanNumber: 'Enter a valid PAN number (e.g. AAAAA0000A)' }));
                                } else {
                                    setErrors(prev => ({ ...prev, PanNumber: '' }));
                                }
                            }}
                            maxLength={10}
                            placeholder="AAAAA0000A"
                            style={{ ...inputStyle, borderColor: errors.PanNumber ? '#ef4444' : '#d1d5db' }}
                        />
                        {errors.PanNumber && (
                            <small style={{ color: '#ef4444' }}>{errors.PanNumber}</small>
                        )}
                    </div>
                    <div>
                        <label style={labelStyle}>Payment Bank ID</label>
                        <input name="PaymentBankId" type="number" value={formData.PaymentBankId} onChange={handleChange}
                            placeholder="Bank ID" style={inputStyle} />
                    </div>
                </div>

                {/* Bill Prefix Details */}
                {sectionHeader('Bill Prefix Details')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    {[['BToBPrefix', 'B2B / IGST', 'B2B'], ['BToCPrefix', 'B2C / IGST Cus.', 'B2C'], ['QuotationPrefix', 'Quotation', 'QT'],
                    ['SaleOrderPrefix', 'Sale Order', 'SO'], ['PurchaseOrderPrefix', 'Purchase Order', 'PO'], ['PurchaseReturnPrefix', 'Purchase Return', 'PR'],
                    ['SaleReturnPrefix', 'Sale Return', 'SR']].map(([name, label, ph]) => (
                        <div key={name}>
                            <label style={labelStyle}>{label}</label>
                            <input name={name} value={formData[name]} onChange={handleChange} maxLength={5}
                                placeholder={ph} style={inputStyle} />
                        </div>
                    ))}
                </div>
                {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
                    {[['QuotationPrefix','Quotation','QT'],['SaleOrderPrefix','Sale Order','SO'],['PurchaseOrderPrefix','Purchase Order','PO'],['PurchaseReturnPrefix','Purchase Return','PR'],['SaleReturnPrefix','Sale Return','SR']].map(([name, label, ph]) => (
                        <div key={name}>
                            <label style={labelStyle}>{label}</label>
                            <input name={name} value={formData[name]} onChange={handleChange} maxLength={5}
                                placeholder={ph} style={inputStyle} />
                        </div>
                    ))}
                </div> */}

                {/* Opening Balances */}
                {sectionHeader('Opening Balances')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                        <label style={labelStyle}>Cash Opening Balance</label>
                        <input name="CashOpeningBalance" type="number" value={formData.CashOpeningBalance} onChange={handleChange}
                            placeholder="0.00" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Cash Opening Date</label>
                        <input name="CashOpeningDate" type="date" value={formData.CashOpeningDate} onChange={handleChange}
                            style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Stock Opening Balance</label>
                        <input name="StockOpeningBalance" type="number" value={formData.StockOpeningBalance} onChange={handleChange}
                            placeholder="0.00" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Stock Opening Date</label>
                        <input name="StockOpeningDate" type="date" value={formData.StockOpeningDate} onChange={handleChange}
                            style={inputStyle} />
                    </div>
                </div>

                {/* E-Invoicing Details */}
                {sectionHeader('E-Invoicing Details')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <FormControlLabel
                        control={<Switch checked={formData.IsEInvoicingEnabled} onChange={handleChange} name="IsEInvoicingEnabled" size="small" />}
                        label={<span style={{ fontSize: '13px' }}>E-Invoicing Enabled</span>}
                    />
                    <FormControlLabel
                        control={<Switch checked={formData.IsEWaybillEnabled} onChange={handleChange} name="IsEWaybillEnabled" size="small" />}
                        label={<span style={{ fontSize: '13px' }}>E-Waybill Enabled</span>}
                    />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                        <label style={labelStyle}>E-Invoice Portal Username</label>
                        <input name="EinvoicePortalUserName" value={formData.EinvoicePortalUserName} onChange={handleChange} maxLength={100}
                            placeholder="Portal username" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>E-Invoice Portal Password</label>
                        <input name="EinvoicePortalPassword" type="password" value={formData.EinvoicePortalPassword} onChange={handleChange} maxLength={100}
                            placeholder="Portal password" style={inputStyle} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                        <label style={labelStyle}>myGST Cafe Customer ID</label>
                        <input name="myGSTcafeCustomerID" value={formData.myGSTcafeCustomerID} onChange={handleChange} maxLength={100}
                            placeholder="Customer ID" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>myGST Cafe API ID</label>
                        <input name="myGSTcafeApiID" value={formData.myGSTcafeApiID} onChange={handleChange} maxLength={100}
                            placeholder="API ID" style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>myGST Cafe API Secret</label>
                        <input name="myGSTcafeApiSecret" type="password" value={formData.myGSTcafeApiSecret} onChange={handleChange} maxLength={100}
                            placeholder="API Secret" style={inputStyle} />
                    </div>
                </div>
            </DialogContent>

            <div style={{
                padding: '10px 16px', borderTop: '1px solid #f0f0f0',
                display: 'flex', justifyContent: 'flex-end', gap: '8px', background: 'white'
            }}>
                <Button onClick={handleClose} disabled={isSaving}
                    style={{ borderColor: '#2C3E6E', color: '#2C3E6E', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 12px', minHeight: '29px', textTransform: 'none', border: '1px solid #2C3E6E' }}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={isSaving || (!formData.Code || !formData.Name)}
                    style={{
                        background: (isSaving || !formData.Code || !formData.Name) ? '#9ca3af' : '#2C3E6E',
                        color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: '700',
                        padding: '4px 12px', minHeight: '29px', textTransform: 'none', border: 'none',
                        boxShadow: '0 2px 8px rgba(44,62,110,0.3)'
                    }}>
                    {isSaving ? <><CircularProgress size={14} color="inherit" style={{ marginRight: '6px' }} />Creating...</> : 'Create Branch'}
                </Button>
            </div>
        </Dialog>
    );
};

export default BranchCreateModal;

